<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { DateTime } from 'luxon'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { PaginatedOauthGrantActivity, type OauthCallOutcome, type OauthGrantActivity } from '@/types'
import {
  hasAgentValueLabel,
  isKnownAgentTool,
  isOversize,
  splitAgentArgs,
  type AgentArg,
  type AgentArgs
} from '@/utils/agentActivity'

type ActivityRow = OauthGrantActivity & { kept: AgentArgs }

interface ActivityPage {
  rows: ActivityRow[]
  total: number
}

/**
 * One grant's call log as two lists: what the application did above reading,
 * each recorded before it ran, with the arguments and replaced values its tool
 * keeps and any address outside the account it asked to forward mail to; and
 * the requests it was refused. A refused row names the route template and the
 * reason, never the path the application sent.
 */
const props = defineProps<{ grantId: number }>()

const { t, locale } = useI18n()

const calls = ref<ActivityPage>({ rows: [], total: 0 })
const refused = ref<ActivityPage>({ rows: [], total: 0 })
const loading = ref(true)
const loadFailed = ref(false)

const fetchKind = async (kind: 'call' | 'refused'): Promise<ActivityPage> => {
  const res = await useAxios('GET', `/account/grants/${props.grantId}/activity?kind=${kind}&limit=50`, true)
  const parsed = PaginatedOauthGrantActivity.safeParse(res.data)
  if (!parsed.success) throw new Error('unexpected activity response')
  return {
    rows: parsed.data.data.map((row) => ({ ...row, kept: splitAgentArgs(row.args) })),
    total: parsed.data.total
  }
}

const load = async (): Promise<void> => {
  loading.value = true
  loadFailed.value = false
  try {
    const [callPage, refusedPage] = await Promise.all([fetchKind('call'), fetchKind('refused')])
    calls.value = callPage
    refused.value = refusedPage
  } catch (error) {
    handleAxiosError(error, { logPrefix: 'GrantActivity' })
    loadFailed.value = true
  } finally {
    loading.value = false
  }
}

const formatDate = (iso: string): string =>
  DateTime.fromISO(iso).setLocale(locale.value).toLocaleString(DateTime.DATETIME_MED)

// The schema reads a reason this build does not know as none.
const reasonText = (row: OauthGrantActivity): string =>
  t(`dashboard.account.connections.activity.reasons.${row.reason ?? 'other'}`)

/** A tool's label, its own name for one this build does not know, or "direct request" without a tool. */
const callTitle = (row: OauthGrantActivity): string => {
  if (!row.tool) return t('dashboard.account.connections.activity.directRequest')
  return isKnownAgentTool(row.tool) ? t(`dashboard.account.connections.tools.${row.tool}`) : row.tool
}

const argLabel = (arg: AgentArg): string =>
  arg.known ? t(`dashboard.account.connections.activity.args.${arg.name}`) : arg.name

/** A value as text; one too long to keep reads as its length, its digest shown beside it. */
const argValue = ({ name, value }: AgentArg): string => {
  if (isOversize(value)) {
    return t('dashboard.account.connections.activity.oversize', {
      bytes: value.bytes.toLocaleString(locale.value)
    })
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : t('dashboard.account.connections.activity.none')
  }
  if (typeof value === 'boolean') {
    return t(`dashboard.account.connections.activity.${value ? 'yes' : 'no'}`)
  }
  if (hasAgentValueLabel(name, value)) {
    return t(`dashboard.account.connections.activity.values.${name}.${String(value)}`)
  }
  return String(value)
}

const outcomeClass = (outcome: OauthCallOutcome): string => {
  switch (outcome) {
    case 'ok':
      return 'status-success'
    case 'attempted':
      return 'status-info'
    case 'denied':
    case 'unknown':
      return 'status-warning'
    default:
      return 'status-error'
  }
}

onMounted(load)
</script>

<template>
  <div class="activity">
    <div v-if="loading" class="activity-state">
      <v-progress-circular indeterminate size="24" width="3" color="#00B0FF" />
    </div>

    <p v-else-if="loadFailed" class="activity-state muted" data-test="activity-failed">
      {{ $t('dashboard.account.connections.activity.loadFailed') }}
    </p>

    <template v-else>
      <h4 class="activity-title">{{ $t('dashboard.account.connections.activity.callsTitle') }}</h4>
      <p class="muted hint">{{ $t('dashboard.account.connections.activity.callsHint') }}</p>
      <p v-if="calls.rows.length === 0" class="muted" data-test="calls-empty">
        {{ $t('dashboard.account.connections.activity.callsEmpty') }}
      </p>
      <div v-else class="activity-list">
        <div v-for="row in calls.rows" :key="row.id" class="activity-row call" data-test="call">
          <div class="call-head">
            <span class="call-title" data-test="call-title">{{ callTitle(row) }}</span>
            <code class="mono muted">{{ row.tool || `${row.method} ${row.route}` }}</code>
            <span
              class="status-chip"
              :class="outcomeClass(row.outcome)"
              :title="row.status ? `HTTP ${row.status}` : undefined"
              data-test="call-outcome"
            >{{ $t(`dashboard.account.connections.activity.outcomes.${row.outcome}`) }}</span>
            <span class="muted">{{ formatDate(row.created_at) }}</span>
          </div>
          <p v-if="row.outcome === 'unknown'" class="muted" data-test="call-unknown">
            {{ $t('dashboard.account.connections.activity.unknownHint') }}
          </p>
          <!-- Values the application wrote, so text interpolation only. -->
          <p
            v-if="row.kept.external.length"
            class="call-external status-warning"
            data-test="call-external"
          >
            <span>{{ $t('dashboard.account.connections.activity.externalTargets') }}</span>
            <span class="mono">{{ row.kept.external.join(', ') }}</span>
          </p>
          <ul v-if="row.kept.given.length" class="call-args" data-test="call-args">
            <li v-for="arg in row.kept.given" :key="arg.name">
              <span class="arg-label">{{ argLabel(arg) }}</span>
              <span class="mono">{{ argValue(arg) }}</span>
              <span v-if="isOversize(arg.value)" class="muted digest" data-test="arg-digest">
                {{ $t('dashboard.account.connections.activity.digest') }}
                <span class="mono">{{ arg.value.sha256 }}</span>
              </span>
            </li>
          </ul>
          <div v-if="row.kept.previous.length" class="call-previous" data-test="call-previous">
            <span class="arg-label">{{ $t('dashboard.account.connections.activity.previous') }}</span>
            <ul class="call-args">
              <li v-for="arg in row.kept.previous" :key="arg.name">
                <span class="arg-label">{{ argLabel(arg) }}</span>
                <span class="mono">{{ argValue(arg) }}</span>
                <span v-if="isOversize(arg.value)" class="muted digest" data-test="arg-digest">
                  {{ $t('dashboard.account.connections.activity.digest') }}
                  <span class="mono">{{ arg.value.sha256 }}</span>
                </span>
              </li>
            </ul>
          </div>
          <p v-if="row.kept.truncated" class="muted" data-test="call-truncated">
            {{ $t('dashboard.account.connections.activity.argsTruncated') }}
          </p>
        </div>
      </div>

      <p v-if="calls.total > calls.rows.length" class="muted">
        {{ $t('dashboard.account.connections.activity.truncated', { shown: calls.rows.length, total: calls.total }) }}
      </p>

      <h4 class="activity-title">{{ $t('dashboard.account.connections.activity.refusedTitle') }}</h4>
      <p class="muted hint">{{ $t('dashboard.account.connections.activity.refusedHint') }}</p>
      <p v-if="refused.rows.length === 0" class="muted" data-test="refused-empty">
        {{ $t('dashboard.account.connections.activity.refusedEmpty') }}
      </p>
      <div v-else class="activity-list">
        <div v-for="row in refused.rows" :key="row.id" class="activity-row refused" data-test="refused">
          <code class="mono">{{ row.method }} {{ row.route || $t('dashboard.account.connections.activity.noRoute') }}</code>
          <span>{{ reasonText(row) }}</span>
          <span v-if="row.required_scopes?.length" class="muted">
            {{ $t('dashboard.account.connections.activity.needed', { scopes: row.required_scopes.join(', ') }) }}
          </span>
          <span class="muted">{{ formatDate(row.created_at) }}, {{ row.client_ip }}</span>
        </div>
      </div>
      <p v-if="refused.total > refused.rows.length" class="muted" data-test="refused-truncated">
        {{ $t('dashboard.account.connections.activity.truncated', { shown: refused.rows.length, total: refused.total }) }}
      </p>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.activity {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(120, 163, 255, 0.08);
}

.activity-state {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.activity-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.8);
  margin: 12px 0 6px;
}

.activity-list {
  display: flex;
  flex-direction: column;
}

.activity-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  // Values an application wrote can stack combining marks past their line.
  overflow: hidden;

  &:last-child {
    border-bottom: none;
  }
}

.activity-row.call {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}

.call-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.call-title {
  font-weight: 500;
}

.call-args {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    overflow: hidden;
  }
}

.call-previous {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 14px;
}

.call-external {
  position: relative;
  z-index: 1;
  overflow: hidden;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin: 0;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 0.85rem;
}

.call-args .digest {
  margin-left: 6px;
}

.arg-label {
  margin-right: 6px;
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.82rem;
}

.mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
  overflow-wrap: anywhere;
}

.muted {
  color: rgba(200, 218, 255, 0.5);
  font-size: 0.82rem;
  margin: 0;
}

.hint {
  margin-bottom: 6px;
}

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
}

.status-success {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-warning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}

.status-info {
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.3);
  color: #4fc3f7;
}

.status-error {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #ef5350;
}
</style>
